import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../lib/format';
import {
  FileText,
  ChevronRight,
  X,
  Package,
  Calendar,
  Truck,
  CreditCard,
  User,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { apiClient } from '../../lib/api';
import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';

// Order interface for the list view
interface Order {
  _id: string;
  orderNumber: string;
  status: 'draft' | 'pending' | 'awaiting_payment' | 'submitted' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'failed';
  totalAmount: number;
  createdAt: string;
}

// Full order detail interface
interface OrderDetail {
  _id: string;
  orderNumber: string;
  status: string;
  totalAmount: number;
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  patientId: {
    _id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone: string;
    address: {
      street: string;
      street2?: string;
      city: string;
      state: string;
      zipCode: string;
    };
  } | string;
  items: Array<{
    productId: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    selectedVariants?: Record<string, string>;
    frozenPricing?: {
      basePrice: number;
      distributorMarkup: number;
      physicianMarkup: number;
      finalPrice: number;
    };
  }>;
  paymentDetails: {
    method: 'physician_billed' | 'patient_pay';
    status: 'not_required' | 'pending' | 'processing' | 'succeeded' | 'failed';
    amount: number;
    currency: string;
    completedAt?: string;
  };
  trackingInfo?: {
    carrier?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    addedAt?: string;
  };
  invoiceId?: {
    _id: string;
    invoiceNumber: string;
    status: string;
  } | string;
}

interface OrderHistoryProps {
  orders: Order[];
  onSelectOrder?: (orderId: string) => void;
}

const statusStyles: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  awaiting_payment: 'bg-orange-100 text-orange-800',
  submitted: 'bg-blue-100 text-blue-800',
  processing: 'bg-indigo-100 text-indigo-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
  failed: 'bg-red-100 text-red-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending: 'Pending',
  awaiting_payment: 'Awaiting Payment',
  submitted: 'Submitted',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

const paymentStatusStyles: Record<string, string> = {
  not_required: 'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-800',
  processing: 'bg-blue-100 text-blue-800',
  succeeded: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

export const OrderHistory: React.FC<OrderHistoryProps> = ({ orders, onSelectOrder }) => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load order detail when modal opens
  useEffect(() => {
    if (selectedOrderId) {
      loadOrderDetail(selectedOrderId);
    }
  }, [selectedOrderId]);

  const loadOrderDetail = async (orderId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.getOrder(orderId);
      if (response.success && response.data) {
        setOrderDetail(response.data);
      } else {
        setError(response.error || 'Failed to load order details');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = (orderId: string) => {
    setSelectedOrderId(orderId);
  };

  const closeModal = () => {
    setSelectedOrderId(null);
    setOrderDetail(null);
    setError(null);
  };

  const formatDate = (dateString: string) => formatDateUtil(dateString);

  const formatDateTime = (dateString: string) => formatDateTimeUtil(dateString);


  // Get patient info from populated or unpopulated patientId
  const getPatientInfo = (order: OrderDetail) => {
    if (typeof order.patientId === 'object' && order.patientId !== null) {
      return order.patientId;
    }
    return null;
  };

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-10">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Order History</h3>
        <p className="mt-1 text-sm text-gray-500">This patient does not have any past orders.</p>
      </div>
    );
  }

  return (
    <>
      {/* Order List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul role="list" className="divide-y divide-gray-200">
          {orders.map((order) => (
            <li key={order._id}>
              <button
                onClick={() => handleOrderClick(order._id)}
                className="block w-full text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors"
              >
                <div className="flex items-center px-4 py-4 sm:px-6">
                  <div className="min-w-0 flex-1 flex items-center">
                    <div className="min-w-0 flex-1 md:grid md:grid-cols-3 md:gap-4">
                      <div>
                        <p className="text-sm font-medium text-indigo-600 truncate">
                          Order #{order.orderNumber}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusStyles[order.status] || statusStyles.pending}`}>
                            {statusLabels[order.status] || order.status}
                          </span>
                        </p>
                      </div>
                      <div className="hidden md:block">
                        <p className="text-sm text-gray-900">
                          {formatDate(order.createdAt)}
                        </p>
                        <p className="mt-2 text-sm text-gray-500">
                          {formatCurrency(order.totalAmount)}
                        </p>
                      </div>
                      {/* Mobile: Show date and amount */}
                      <div className="md:hidden mt-2">
                        <p className="text-sm text-gray-500">
                          {formatDate(order.createdAt)} • {formatCurrency(order.totalAmount)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Order Detail Modal */}
      {selectedOrderId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={closeModal}
            />

            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                      <Package className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Order Details
                      </h3>
                      {orderDetail && (
                        <p className="text-sm text-gray-500">
                          #{orderDetail.orderNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[70vh] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
                    <span className="ml-2 text-gray-600">Loading order details...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600">{error}</p>
                    <button
                      onClick={() => loadOrderDetail(selectedOrderId)}
                      className="mt-4 text-indigo-600 hover:text-indigo-800"
                    >
                      Try again
                    </button>
                  </div>
                ) : orderDetail ? (
                  <div className="space-y-6">
                    {/* Status & Date */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`px-3 py-1 text-sm font-semibold rounded-full ${statusStyles[orderDetail.status] || statusStyles.pending}`}>
                        {statusLabels[orderDetail.status] || orderDetail.status}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Created: {formatDateTime(orderDetail.createdAt)}
                      </span>
                      {orderDetail.submittedAt && (
                        <span className="text-sm text-gray-500 flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          Submitted: {formatDateTime(orderDetail.submittedAt)}
                        </span>
                      )}
                    </div>

                    {/* Patient Info */}
                    {getPatientInfo(orderDetail) && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <User className="h-4 w-4 mr-2 text-gray-500" />
                          Patient Information
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-500">Name:</span>{' '}
                            <span className="text-gray-900 font-medium">
                              {getPatientInfo(orderDetail)!.firstName} {getPatientInfo(orderDetail)!.lastName}
                            </span>
                          </div>
                          {getPatientInfo(orderDetail)!.phone && (
                            <div>
                              <span className="text-gray-500">Phone:</span>{' '}
                              <span className="text-gray-900">{getPatientInfo(orderDetail)!.phone}</span>
                            </div>
                          )}
                          {getPatientInfo(orderDetail)!.email && (
                            <div className="sm:col-span-2">
                              <span className="text-gray-500">Email:</span>{' '}
                              <span className="text-gray-900">{getPatientInfo(orderDetail)!.email}</span>
                            </div>
                          )}
                          {getPatientInfo(orderDetail)!.address && (
                            <div className="sm:col-span-2 flex items-start">
                              <MapPin className="h-4 w-4 text-gray-400 mr-1 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-900">
                                {[getPatientInfo(orderDetail)!.address.street, getPatientInfo(orderDetail)!.address.street2].filter(Boolean).join(', ')}, {getPatientInfo(orderDetail)!.address.city}, {getPatientInfo(orderDetail)!.address.state} {getPatientInfo(orderDetail)!.address.zipCode}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Prescription Items */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-gray-500" />
                        Prescription Items
                      </h4>
                      <div className="border rounded-lg divide-y">
                        {orderDetail.items.map((item, index) => (
                          <div key={index} className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{item.productName}</p>
                                {item.selectedVariants && Object.keys(item.selectedVariants).length > 0 && (
                                  <div className="mt-1 flex flex-wrap gap-2">
                                    {Object.entries(item.selectedVariants).map(([key, value]) => (
                                      <span key={key} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        {key}: {value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <p className="text-sm text-gray-500 mt-1">
                                  Qty: {item.quantity} × {formatCurrency(item.unitPrice)}
                                </p>
                              </div>
                              <p className="font-semibold text-gray-900 ml-4">
                                {formatCurrency(item.totalPrice)}
                              </p>
                            </div>
                          </div>
                        ))}
                        {/* Total */}
                        <div className="p-4 bg-gray-50">
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-gray-900">Total</span>
                            <span className="text-lg font-bold text-gray-900">
                              {formatCurrency(orderDetail.totalAmount)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        <CreditCard className="h-4 w-4 mr-2 text-gray-500" />
                        Payment Information
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Method:</span>{' '}
                          <span className="text-gray-900 font-medium">
                            {orderDetail.paymentDetails.method === 'patient_pay' ? 'Patient Pay' : 'Physician Billed'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Status:</span>{' '}
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${paymentStatusStyles[orderDetail.paymentDetails.status] || paymentStatusStyles.pending}`}>
                            {orderDetail.paymentDetails.status === 'not_required' ? 'Not Required' : 
                             orderDetail.paymentDetails.status.charAt(0).toUpperCase() + orderDetail.paymentDetails.status.slice(1)}
                          </span>
                        </div>
                        {orderDetail.paymentDetails.completedAt && (
                          <div className="sm:col-span-2">
                            <span className="text-gray-500">Paid on:</span>{' '}
                            <span className="text-gray-900">{formatDateTime(orderDetail.paymentDetails.completedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Tracking Info */}
                    {orderDetail.trackingInfo && orderDetail.trackingInfo.trackingNumber && (
                      <div className="bg-purple-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <Truck className="h-4 w-4 mr-2 text-purple-500" />
                          Shipping & Tracking
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          {orderDetail.trackingInfo.carrier && (
                            <div>
                              <span className="text-gray-500">Carrier:</span>{' '}
                              <span className="text-gray-900 font-medium">{orderDetail.trackingInfo.carrier}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">Tracking #:</span>{' '}
                            <span className="text-gray-900 font-medium">{orderDetail.trackingInfo.trackingNumber}</span>
                          </div>
                          {orderDetail.trackingInfo.trackingUrl && (
                            <div className="sm:col-span-2">
                              <a
                                href={orderDetail.trackingInfo.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-indigo-600 hover:text-indigo-800"
                              >
                                Track Package <ExternalLink className="h-4 w-4 ml-1" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Invoice Info */}
                    {orderDetail.invoiceId && typeof orderDetail.invoiceId === 'object' && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-gray-500" />
                          Invoice
                        </h4>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {orderDetail.invoiceId.invoiceNumber}
                            </p>
                            <p className="text-sm text-gray-500">
                              Status: <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                orderDetail.invoiceId.status === 'paid' ? 'bg-green-100 text-green-800' :
                                orderDetail.invoiceId.status === 'voided' ? 'bg-gray-100 text-gray-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>{orderDetail.invoiceId.status}</span>
                            </p>
                          </div>
                          <a
                            href={`/invoice-tracking?invoiceId=${orderDetail.invoiceId._id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 text-sm"
                          >
                            View Invoice <ExternalLink className="h-4 w-4 ml-1" />
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Success indicator for delivered orders */}
                    {orderDetail.status === 'delivered' && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                        <span className="text-green-800 font-medium">This order has been delivered successfully.</span>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
                {onSelectOrder && orderDetail && (
                  <button
                    type="button"
                    onClick={() => {
                      closeModal();
                      onSelectOrder(orderDetail._id);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    View Full Details
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
